import React, { useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import AppHeader from '@/components/AppHeader';
import { extractErrorMessage } from '@/lib/errors';
import { ArrowLeft, Loader2 } from 'lucide-react';

const OTP_LENGTH = 6;

/**
 * Step 2 of password reset: user types the 6-digit OTP they received
 * + a new password (twice, so a typo doesn't lock them out). On
 * success we route them BACK to /login with a success toast — we do
 * NOT auto-sign-in. The point of a reset is for the user to
 * explicitly prove they can type the new password; auto-login would
 * also hide a "did my new password save correctly" moment.
 *
 * The phone comes in via `location.state` from the request page; if
 * someone hits this URL directly we bounce them back to step 1 so
 * they can't bypass the recaptcha + rate-limit on /request.
 */
export default function ResetPasswordConfirmPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const location = useLocation();
  const phone = location.state?.phone || '';

  const otpInputs = useRef([]);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!phone) {
    // No phone in state → user hit this page directly. Send them back
    // to step 1 so they can't bypass the request flow.
    return <Navigate to="/forgot-password" replace />;
  }

  const handleOtpChange = (idx, raw) => {
    const digit = (raw || '').replace(/\D/g, '').slice(0, 1);
    const next = [...otp];
    next[idx] = digit;
    setOtp(next);
    if (digit && idx < OTP_LENGTH - 1) {
      otpInputs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpInputs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('').map((_, i) => text[i] || '');
    setOtp(next);
    const lastFilled = Math.min(text.length, OTP_LENGTH) - 1;
    otpInputs.current[lastFilled]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      toast({ title: t('auth.resetConfirm.errors.otpShort', { defaultValue: 'Enter the full 6-digit code.' }), variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: t('auth.register.errors.shortPassword'), variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t('auth.register.errors.mismatch'), variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.confirmPasswordReset({
        phone,
        otp: code,
        new_password: newPassword,
      });
      toast({
        title: t('auth.resetConfirm.success', { defaultValue: 'Password reset. Please sign in with your new password.' }),
      });
      navigate('/login');
    } catch (error) {
      if (error?.response?.status === 429) {
        toast({
          title: t('auth.resetConfirm.errors.rateLimited', {
            defaultValue: 'Too many attempts. Please wait a few minutes and try again.',
          }),
          variant: 'destructive',
        });
      } else if (error?.response?.status === 503) {
        // Reset by SMS is off (no SMS provider yet): point to support.
        toast({ title: t('auth.resetRequest.errors.unavailable'), variant: 'destructive' });
      } else {
        toast({
          title: extractErrorMessage(error, t('auth.resetConfirm.errors.failed', {
            defaultValue: 'The code is wrong or expired. Request a new one and try again.',
          })),
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Last 4 digits for the user-visible hint ("Code sent to **** 4567").
  const maskedPhone = phone.replace(/.(?=.{4})/g, '•');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader trailing={<LanguagePickerButton />} />

      <div className="flex-1 flex flex-col p-6">
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="flex items-center gap-2 text-sm text-gray-600 mb-4 hover:text-gray-900"
          data-testid="reset-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {t('auth.resetConfirm.back', { defaultValue: 'Use a different number' })}
        </button>

        <div className="text-center mt-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.resetConfirm.title', { defaultValue: 'Set your new password' })}
          </h1>
          <p className="text-gray-600">
            {t('auth.resetConfirm.subtitle', {
              defaultValue: 'Enter the 6-digit code we sent to {{phone}} and choose a new password.',
              phone: maskedPhone,
            })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t('auth.resetConfirm.otpLabel', { defaultValue: 'Verification code' })}
            </label>
            <div className="flex gap-2 justify-between">
              {otp.map((digit, i) => (
                <Input
                  key={i}
                  ref={(el) => { otpInputs.current[i] = el; }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete="one-time-code"
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  className="w-full h-14 text-center text-xl font-semibold bg-white border-gray-200"
                  data-testid={`reset-otp-${i}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="reset-new-password" className="block text-sm font-semibold text-gray-900 mb-2">
              {t('auth.resetConfirm.newPasswordLabel', { defaultValue: 'New password' })}
            </label>
            <Input
              id="reset-new-password"
              type="password"
              placeholder={t('auth.register.passwordPlaceholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full h-14 bg-white border-gray-200 text-lg"
              autoComplete="new-password"
              data-testid="reset-new-password-input"
            />
          </div>

          <div>
            <label htmlFor="reset-confirm-password" className="block text-sm font-semibold text-gray-900 mb-2">
              {t('auth.resetConfirm.confirmLabel', { defaultValue: 'Confirm new password' })}
            </label>
            <Input
              id="reset-confirm-password"
              type="password"
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full h-14 bg-white border-gray-200 text-lg"
              autoComplete="new-password"
              data-testid="reset-confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-farm-primary hover:bg-farm-primary-dark text-white font-semibold text-lg rounded-xl"
            data-testid="reset-confirm-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('auth.resetConfirm.submit', { defaultValue: 'Set new password' })
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
